import { useConsumptionData } from "@/api/consumption";
import { formatCurrency } from "@/lib/format-for-view";
import { format, startOfMonth, sub } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const EleSimilarHouseholds = () => {
	const currentMonthDate = sub(new Date(), { days: 2 });

	const dayBefore = sub(new Date(), { days: 2 });

	// Get consumption data
	const {
		data: consumptionData,
		isLoading,
		error,
	} = useConsumptionData({
		energy: "ELEC",
		granularity: "MONTHLY",
		start_date: format(startOfMonth(currentMonthDate), "yyyy-MM-dd"),
		end_date: format(dayBefore, "yyyy-MM-dd"),
		include_baseline: true,
	});

	// Get the latest data point
	const latestData = consumptionData?.data?.[0];

	const data = [
		{
			name: "Votre consommation",
			value: latestData?.cost || 0,
			color: "#E6007E",
		},
		{
			name: "Les 100 meilleurs",
			value: latestData?.top_100_cost || 0,
			color: "#24395E",
		},
		{
			name: "Moyenne",
			value: latestData?.average_100_cost || 0,
			color: "#4EBCC3",
		},
		{
			name: "Les 100 moins performants",
			value: latestData?.worst_100_cost || 0,
			color: "#E76E50",
		},
	];

	const maxValue = Math.max(...data.map((item) => item.value));

	const userConsumption =
		data.find((item) => item.name === "Votre consommation")?.value || 0;
	const averageConsumption =
		data.find((item) => item.name === "Moyenne")?.value || 0;

	const indicatorText =
		userConsumption > averageConsumption
			? "Votre consommation est supérieure à la moyenne des foyers similaires de votre région."
			: "Bonne nouvelle ! Votre consommation est inférieure à la moyenne des foyers similaires de votre région.";

	if (consumptionData?.data.length === 0) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Aucune donnée trouvée pour cette période
					</div>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Erreur lors du chargement des données de consommation. Veuillez
						réessayer ultérieurement.
					</div>
				</CardContent>
			</Card>
		);
	}

	if (isLoading) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="animate-pulse space-y-4">
						<div className="h-4 bg-gray-200 rounded w-1/4" />
						<div className="h-6 bg-gray-200 rounded w-3/4" />
						<div className="h-8 bg-gray-200 rounded w-1/2" />
						<div className="h-4 bg-gray-200 rounded w-2/3" />
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className="max-w-md mx-auto p-3 sm:p-4 gap-4 space-y-4">
			<CardHeader className="flex flex-row items-start justify-between p-0 pb-2">
				<div>
					<CardTitle className="text-md font-semibold">
						Comparaison avec des foyers similaires
					</CardTitle>
					<p className="text-xs text-gray-500">Mois en cours </p>
				</div>
			</CardHeader>

			<div className="border rounded-lg p-2 sm:p-3 mb-3">
				<div className="flex items-center gap-1 sm:gap-2">
					<div className=" w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex items-center justify-center">
						<span className="text-sm">ⓘ</span>
					</div>
					<span className="font-semibold text-sm sm:text-base">
						Indicateurs de performance
					</span>
				</div>
				<p className="text-xs sm:text-sm text-gray-600 mt-0.5 pl-6 sm:pl-8">
					{indicatorText}
				</p>
			</div>

			<div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
				{data.map((item) => (
					<div key={item.name} className="flex flex-col">
						<span className="text-xs sm:text-sm mb-0.5">{item.name}</span>
						<div
							className="h-6 sm:h-8 rounded-md flex items-center justify-center text-white text-xs sm:text-sm"
							style={{
								backgroundColor: item.color,
								width: `${(item.value / maxValue) * 100}%`,
							}}
						>
							{`${formatCurrency(item.value)}`}
						</div>
					</div>
				))}
			</div>
		</Card>
	);
};

export default EleSimilarHouseholds;
